import {_} from 'lodash';
import {IAuthenticationType, IFormTemplate, IFormTemplatePage, IResponse, IRouteDefinitionParams, ITableColumn} from '../../../index';
import {ICell, tpl, viewModel as SearchViewModel} from "../../search/search";
import {global, lm} from "../../../appContext";
import {
    copyToClipboard,
    createErrorMessage,
    decodeAndStripHtml,
    decodeHtml,
    isBit,
    isSupportedCircle,
    stripHtml
} from "../../../util/misc";
import {REQUEST_THROTTLE} from "../../../constants";
import {nsFormTemplate} from "./nsFormTemplate";
import {IRequestObject} from "../../../req/index";
import {getHash, setRouteHash} from "../../../constants/RouteDefinitions";
import {ISearchRowButtons} from "../../search/index";
import {EMPTY_STRING} from "../../../constants/Language";
import {IParams} from "../../button/ellipsis-dropdown"
import {ISplitButtonParams} from "@component/button/splitButton/split-button";
import trashDialog = nsFormTemplate.trashDialog;
import copyDialog = nsFormTemplate.copyDialog;
import openFormDialog = nsFormTemplate.openFormDialog;
import openFormDialog_v2 = nsFormTemplate.openFormDialog_v2;
import openV2form = nsFormTemplate.openV2form;
import openV1form = nsFormTemplate.openV1form;

export let template = tpl;

export class viewModel<T = IFormTemplate> extends SearchViewModel {

    protected override getSearchSplitButtonParams(): ISplitButtonParams | null {
        return {
            mainButton: {
                cls: 'btn-action btn-sm',
                icon: 'fa-plus',
                label: 'btn.create.formtemplate',
                onClick: () => openV2form(),
            }
        }
    }

    protected readonly TABLE: ITableColumn[] = [
        {key: 'Id', label: 'id'},
        {key: 'Name', label: 'name'},
        {key: 'authenticationtypes', label: 'Authentication type|p', count: true},
        {key: 'pages', label: 'page|p', count: true},
        {key: 'active', label: 'active', yesno: true, center: true},
        {key: 'published', label: 'Published', yesno: true, center: true},
        {key: 'version', label: 'version', center: true, getContent: o => (o.v2form ? 2 : 1).toString()},
        {key: "func", label: ""},

    ];
    private authenticationTypes!: IAuthenticationType[];
    protected override fnRow: ISearchRowButtons[] = [
        {
            icon: 'fa-ellipsis-v',
            title: 'function|p',
            getComponent: (formTemplate: IFormTemplate) => {
                const params: IParams = {
                    buttons: [
                        {
                            label: 'edit',
                            onClick: () => {
                                if (formTemplate.v2form) {
                                    openV2form(formTemplate, {mode: 'edit'})
                                } else {
                                    setRouteHash('editformtemplate', formTemplate)
                                }
                            },
                            validator: () => global.permissionValidator.isMinCompanyAdmin(),
                        },
                        {
                            label: 'copy',
                            onClick: () => copyDialog(formTemplate, (reqObj: IRequestObject) => {
                                this.isWaiting(true);
                                reqObj.request
                                    .then(() => {
                                        this.isWaiting(false);
                                        this.preSubmit();
                                    });
                            }, {cb_newName: (o: IFormTemplate) => o.v2form ? o.name : '' /* empty => fallback to default */}),
                            validator: () => global.permissionValidator.isMinCompanyAdmin(),
                        },
                        {
                            label: `preview`,
                            onClick: () => openV2form(formTemplate, {mode: 'preview'}),
                            validator: () => !!(global.permissionValidator.isMinCompanyAdmin() && formTemplate.v2form),
                        },
                        {
                            label: 'response|p',
                            onClick: () => openV2form(formTemplate, {mode: 'responses'}),
                            validator: () => !!(global.permissionValidator.isMinCompanyAdmin() && formTemplate.v2form),
                        },
                        {
                            label: `copy subject to clipboard|s:((form|ac)) ((url))`,
                            onClick: () => {
                                const hash = getHash('formview_v2', {
                                    orgnr: formTemplate.companyid,
                                    id: formTemplate.id,
                                });
                                const url = new URL(document.location.href)
                                url.hash = hash ?? ''
                                copyToClipboard(url)
                            },
                            validator: () => !!formTemplate.v2form,
                        },
                        {
                            label: `open subject|s:((form|ac))`,
                            onClick: () => formTemplate.v2form ? openFormDialog_v2(formTemplate): openFormDialog(formTemplate),
                            validator: () => global.permissionValidator.isMinCompanyAdmin(),
                        },
                        {
                            label: `delete`,
                            cls: 'btn btn-danger btn-ready-calm',
                            onClick: () => trashDialog(formTemplate, (reqObj: IRequestObject) => {
                                this.isDeleting(true);
                                reqObj.request
                                    .then(() => {
                                        this.isDeleting(false);
                                        this.preSubmit();
                                    })
                            }),
                            validator: () => global.permissionValidator.isMinCompanyAdmin(),
                        },
                    ]
                };
                return {
                    name: 'component/button/ellipsis-dropdown',
                    params: params
                }
            },
        },
    ];

    constructor(params: IRouteDefinitionParams) {
        super(params);
    }

    protected override onSubmit(throttleDuration: number = REQUEST_THROTTLE) {
        if (!global.login.selectedOrganization() || !this.hasSearchKey()) {
            this.resetSearch();
            return;
        }
        let reqGetAuthenticationTypes = global.getPromiseRequest('getauthenticationtypes');
        reqGetAuthenticationTypes
            .request()
            .then((data: IAuthenticationType[]) => {
                this.authenticationTypes = _.sortBy(data, (t: IAuthenticationType) => t.name.toLowerCase());
            })
            .then(() =>
                this.reqSearch.request(this.getParams(), true, undefined, undefined, throttleDuration)
                    .then((resp: IResponse<ICell['item']>) => this.onSearchSuccess(resp))
                    .catch((resp: IResponse) => {
                        const reason = String(resp?.lasterror ?? resp);
                        this._searchMessage(createErrorMessage(String(reason)))
                        this.resetSearch()
                    })
            )
            .catch((reason: string) => this._searchMessage(createErrorMessage(String(reason))))
        ;

    }

    protected override getCellText(obj: ICell): string {
        const val = this.getCellVal(obj)
        switch (obj.key) {
            case 'active':
            case 'published':
            case 'manual':
            case 'targetself':
                return lm.t(val ? 'Yes' : 'No') ?? ''
            case 'authenticationtypes':
                const authenticationTypes = this.authenticationTypes.filter(at => isBit(at.flag, val));
                return 'Autentiseringstyper:\n' + authenticationTypes.map(at => '• ' + at.name).join('\n');
            case 'Name':
                return stripHtml(decodeHtml(val))
            case 'pages': {
                if (obj.item.v2form) {
                    const pages = _.sortBy(obj.item.pages, 'order')
                    const numPages = pages.length
                    let ret = lm.t(`${numPages} ((page|n:${numPages})):\n`) ?? ''
                    ret += pages
                        .map((p: IFormTemplate['pages'][number]) => {
                            let description = decodeAndStripHtml(p.description)
                            if (description === '') {
                                description = lm.t(EMPTY_STRING) ?? ''
                            }
                            return '• ' + description
                        })
                        .join('\n')
                    return ret
                }
                const numPages = val.length
                return (lm.t(`${numPages} ((page|n:${numPages})):\n`) ?? '') + _.sortBy(val, 'order')
                    .map((p: IFormTemplatePage) => '• ' + p.description).join('\n');
            }
        }
        return super.getCellText(obj);
    }

    protected override getCellContent(obj: ICell): string | number {
        const val = this.getCellVal(obj)
        switch (obj.key) {
            case 'active':
            case 'published':
                return isSupportedCircle(val);
            case 'authenticationtypes':
                const authenticationTypes = this.authenticationTypes.filter(at => isBit(at.flag, val));
                return authenticationTypes.length;
            case 'pages':
                return super.getCellText(obj);
        }
        return super.getCellContent(obj);
    }

    protected override onRowClick(item: IFormTemplate) {
        if (item.v2form) {
            openV2form(item, {mode: 'edit', version: item.v2form ? 2: 1})
        } else {
            openV1form(item)
        }
    }

}
